import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { CheckCircleIcon, ChevronUpDownIcon, ExclamationTriangleIcon, LightBulbIcon, ScissorsIcon, UserIcon } from "@heroicons/react/24/outline";
import { useMemo, useRef, useState } from "react";
import { anteilBetraege, pruefeAufteilung, VOLL_BP } from "../../lib/rechnung.ts";
import { formatCurrency } from "../lib/format";
import { useDeleteAnteile, useSetAnteile, useSetBuchungsart, type Buchungsart, type LineItemDetail } from "../lib/receipts";
import { useToast } from "./Toast";

/**
 * Oberflächen-Paket (2026-10-17): Buchungsart einer Position — fünf Zustände, nie nur über Farbe unterschieden
 * (Rot-Grün-Schwäche: Symbol + Text, kein Grün):
 *   leer → ⚠ „Buchungsart wählen“ · Vorschlag → 💡 „Vorschlag · Standard von …“ · bestätigt → ✓ „bestätigt“
 *   selbst gewählt → 👤 „selbst gewählt“ + „zurück zum Vorschlag“ · aufgeteilt → ✂ Teile mit Beträgen + „ändern“ / „aufheben“
 * Auswahl nur aus den Buchungsarten des Steuerjahres; letzte Option „Aufteilen …“.
 */

type Zustand = "leer" | "vorschlag" | "bestaetigt" | "gewaehlt" | "aufgeteilt";

export function buchungsartZustand(item: LineItemDetail): Zustand {
  if (item.anteile.length > 0) return "aufgeteilt";
  if (!item.steuerArtId) return "leer";
  if (item.hasSteuerArtOverride) return "gewaehlt";
  return item.steuerArtBestaetigt ? "bestaetigt" : "vorschlag";
}

const EBENE: Record<string, string> = { kategorie: "Kategorie", unterkategorie: "Unterkategorie", "unter-unterkategorie": "Unterkategorie" };
const AUFTEILEN = "__aufteilen__";

export function BuchungsartFeld({ receiptId, item, arten, betrag, currency, jahrFehlt, steuerjahr }: {
  receiptId: string;
  item: LineItemDetail;
  arten: Buchungsart[];
  betrag: number; // effektiver Betrag der Position (nach Rabatten) — Basis der Teilbeträge
  currency: string;
  jahrFehlt: boolean;
  steuerjahr: number | null;
}) {
  const toast = useToast();
  const setArt = useSetBuchungsart(receiptId);
  const loeschen = useDeleteAnteile(receiptId);
  const [dialog, setDialog] = useState(false);
  const name = useMemo(() => new Map(arten.map((a) => [a.id, a.name])), [arten]);
  const zustand = buchungsartZustand(item);
  const fehler = (e: unknown) => toast.error(e instanceof Error ? e.message : String(e));

  if (jahrFehlt) {
    return (
      <div className="flex items-center gap-1 text-[13px] text-ios-blue">
        <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden />
        <span>Für {steuerjahr ?? "dieses Jahr"} sind keine Buchungsarten angelegt</span>
      </div>
    );
  }

  const waehle = (id: string) => {
    if (id === AUFTEILEN) {
      // Fokus aus dem Auswahlfeld nehmen, sonst bleibt dessen Liste über dem Dialog offen.
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      setDialog(true);
      return;
    }
    setArt.mutate({ itemId: item.id, steuerArtId: id }, { onError: fehler });
  };

  return (
    <div className="min-w-0">
      {zustand === "aufgeteilt" ? (
        <div className="text-[14px]">
          <div className="flex items-start gap-1">
            <ScissorsIcon className="h-4 w-4 mt-0.5 shrink-0 text-ios-blue" aria-hidden />
            <span>
              <span className="font-medium">Aufgeteilt: </span>
              {anteilBetraege(betrag, item.anteile).map((b, i) => {
                const t = item.anteile[i]!;
                return (
                  <span key={t.reihenfolge}>
                    {i > 0 && " · "}
                    {name.get(t.steuerArtId) ?? "?"} {formatProzent(t.anteilBp)} ({formatCurrency(b, currency)})
                  </span>
                );
              })}
            </span>
          </div>
          <div className="mt-0.5 flex gap-3 text-[12px]">
            {item.anteile.some((a) => a.herkunft === "regel") && <span className="text-ios-muted">Vorschlag aus Regel</span>}
            <button type="button" className="text-ios-blue" onClick={() => setDialog(true)}>ändern</button>
            <button type="button" className="text-ios-blue" onClick={() => loeschen.mutate(item.id, { onError: fehler })}>aufheben</button>
          </div>
        </div>
      ) : (
        <>
          <ArtAuswahl
            wert={item.steuerArtId}
            arten={arten}
            leer={zustand === "leer"}
            hervorheben={zustand === "gewaehlt"}
            onWahl={waehle}
          />
          <StatusZeile item={item} zustand={zustand} onZurueck={() => setArt.mutate({ itemId: item.id, steuerArtId: "vorschlag" }, { onError: fehler })} />
        </>
      )}
      {dialog && (
        <AufteilenDialog
          receiptId={receiptId}
          item={item}
          arten={arten}
          betrag={betrag}
          currency={currency}
          onClose={() => setDialog(false)}
        />
      )}
    </div>
  );
}

function StatusZeile({ item, zustand, onZurueck }: { item: LineItemDetail; zustand: Zustand; onZurueck: () => void }) {
  const klein = "mt-0.5 flex items-center gap-1 text-[12px]";
  if (zustand === "leer")
    return (
      <div className={`${klein} text-ios-blue`}>
        <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden /> Pflichtfeld — kein Standard für diese Kategorie
      </div>
    );
  if (zustand === "vorschlag") {
    const q = item.vorschlag.quelle;
    return (
      <div className={`${klein} text-ios-blue`}>
        <LightBulbIcon className="h-3.5 w-3.5" aria-hidden />
        <span>Vorschlag{q ? ` · Standard von ${EBENE[q.ebene] ?? "Kategorie"} „${q.name}“` : ""} — wird mit „Prüfung abschließen“ bestätigt</span>
      </div>
    );
  }
  if (zustand === "bestaetigt")
    return (
      <div className={`${klein} text-ios-green`}>
        <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden /> bestätigt
      </div>
    );
  return (
    <div className={`${klein} text-ios-green`}>
      <UserIcon className="h-3.5 w-3.5" aria-hidden /> selbst gewählt
      <button type="button" className="ml-2 underline" onClick={onZurueck}>zurück zum Vorschlag</button>
    </div>
  );
}

function ArtAuswahl({ wert, arten, leer, hervorheben, onWahl, ohneAufteilen }: {
  wert: string | null;
  arten: Buchungsart[];
  leer?: boolean;
  hervorheben?: boolean;
  onWahl: (id: string) => void;
  ohneAufteilen?: boolean;
}) {
  const [query, setQuery] = useState("");
  const knopf = useRef<HTMLButtonElement>(null);
  const aktuell = arten.find((a) => a.id === wert) ?? null;
  const gefiltert = useMemo(() => {
    const t = query.toLowerCase().split(/\s+/).filter(Boolean);
    return t.length === 0 ? arten : arten.filter((a) => t.every((x) => `${a.name} ${a.topf}`.toLowerCase().includes(x)));
  }, [arten, query]);
  return (
    // Liste öffnet beim KLICK ins Feld (über den Knopf), nicht schon beim Fokus — sonst springt sie nach dem Schließen
    // eines Dialogs (Fokus kehrt zurück) ungefragt wieder auf.
    <Combobox value={aktuell} onClose={() => setQuery("")} onChange={(a: Buchungsart | string | null) => {
      if (a === null) return;
      onWahl(typeof a === "string" ? a : a.id);
    }}>
      <div className="relative">
        <ComboboxInput
          // Beim Klick: vorhandenen Text markieren, damit Tippen ihn ERSETZT (sonst sucht man nach „Privat …geschäftlich“).
          onClick={(e) => { e.currentTarget.select(); knopf.current?.click(); }}
          className={`w-full text-[16px] lg:text-[14px] pl-2 pr-7 py-1 rounded-ios outline-none focus:ring-1 focus:ring-ios-blue truncate ${
            leer ? "bg-ios-blue/10 ring-1 ring-ios-blue/60 placeholder:text-ios-blue" : "bg-ios-bg/60"
          } ${hervorheben ? "font-semibold" : ""}`}
          displayValue={(a: Buchungsart | null) => (a ? a.name : "")}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={leer ? "⚠ Buchungsart wählen" : "Buchungsart suchen"}
          title={aktuell ? `${aktuell.name} — ${aktuell.topf}` : undefined}
        />
        <ComboboxButton ref={knopf} className="absolute inset-y-0 right-0 flex items-center pr-1.5 text-ios-muted" aria-label="Buchungsarten anzeigen">
          <ChevronUpDownIcon className="h-4 w-4" aria-hidden />
        </ComboboxButton>
        <ComboboxOptions
          anchor="bottom start"
          className="z-50 mt-1 min-w-[calc(100vw-2rem)] md:min-w-[24rem] md:w-[var(--input-width)] max-h-80 overflow-auto rounded-ios bg-white shadow-lg ring-1 ring-ios-line/40 py-1 empty:hidden"
        >
          {gefiltert.length === 0 && <div className="px-3 py-2 text-[14px] text-ios-muted">Keine Treffer</div>}
          {gefiltert.map((a) => (
            <ComboboxOption key={a.id} value={a}
              className="cursor-pointer px-3 py-1.5 text-[14px] data-[focus]:bg-ios-blue data-[focus]:text-white data-[selected]:font-semibold">
              <div className="font-medium">{a.name}</div>
              <div className="text-[12px] opacity-70 truncate">{a.topf}</div>
            </ComboboxOption>
          ))}
          {!ohneAufteilen && (
            <ComboboxOption value={AUFTEILEN}
              className="cursor-pointer border-t border-ios-line/40 px-3 py-1.5 text-[14px] text-ios-blue data-[focus]:bg-ios-blue data-[focus]:text-white">
              ✂ Aufteilen … (z. B. 50 % geschäftlich, 50 % privat)
            </ComboboxOption>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}

function formatProzent(bp: number): string {
  return `${(bp / 100).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`;
}

/** "50" / "33,33" / "12.5" → Basispunkte; ungültig → NaN. */
function parseProzent(text: string): number {
  const n = Number(text.replace(",", ".").replace("%", "").trim());
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}

type Zeile = { steuerArtId: string | null; prozent: string };

function AufteilenDialog({ receiptId, item, arten, betrag, currency, onClose }: {
  receiptId: string;
  item: LineItemDetail;
  arten: Buchungsart[];
  betrag: number;
  currency: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const setAnteile = useSetAnteile(receiptId);
  const [zeilen, setZeilen] = useState<Zeile[]>(() =>
    item.anteile.length > 0
      ? item.anteile.map((a) => ({ steuerArtId: a.steuerArtId, prozent: String(a.anteilBp / 100).replace(".", ",") }))
      : [{ steuerArtId: item.steuerArtId, prozent: "50" }, { steuerArtId: null, prozent: "50" }],
  );
  const teile = zeilen.map((z) => ({ steuerArtId: z.steuerArtId ?? "", anteilBp: parseProzent(z.prozent) }));
  const summeBp = teile.reduce((s, t) => s + (Number.isFinite(t.anteilBp) ? t.anteilBp : 0), 0);
  let pruefung: string | null = null;
  try {
    if (teile.some((t) => !t.steuerArtId)) throw new Error("Bitte für jeden Teil eine Buchungsart wählen");
    pruefeAufteilung(teile);
  } catch (e) {
    pruefung = e instanceof Error ? e.message : String(e);
  }
  const vorschau = pruefung === null || summeBp === VOLL_BP ? anteilBetraege(betrag, teile.map((t) => ({ anteilBp: Number.isFinite(t.anteilBp) ? t.anteilBp : 0 }))) : null;
  const setze = (i: number, z: Partial<Zeile>) => setZeilen((alt) => alt.map((x, j) => (j === i ? { ...x, ...z } : x)));

  const speichern = () => {
    if (pruefung) return;
    setAnteile.mutate({ itemId: item.id, teile }, {
      onSuccess: () => { toast.success("Aufteilung gespeichert"); onClose(); },
      onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
    });
  };

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden />
      <div className="fixed inset-0 flex items-end md:items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg rounded-iosLg bg-white p-4 shadow-card">
          <DialogTitle className="text-[17px] font-semibold">Position aufteilen</DialogTitle>
          <p className="mt-1 text-[13px] text-ios-muted">
            „{item.descriptionText}“ — {formatCurrency(betrag, currency)}. Die Position bleibt so, wie sie auf dem Bon steht; nur die
            Steuer-Rechnung zählt die Teile.
          </p>
          <div className="mt-3 space-y-2">
            {zeilen.map((z, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <ArtAuswahl wert={z.steuerArtId} arten={arten} leer={!z.steuerArtId} ohneAufteilen onWahl={(id) => setze(i, { steuerArtId: id })} />
                </div>
                <input
                  // Startfokus im Dialog: das Prozentfeld (ein Auswahlfeld würde beim Fokus sofort seine Liste öffnen).
                  autoFocus={i === 0}
                  className="w-20 bg-ios-bg/60 rounded-ios px-2 py-1 text-right text-[16px] lg:text-[14px] tabular-nums"
                  inputMode="decimal"
                  value={z.prozent}
                  onChange={(e) => setze(i, { prozent: e.target.value })}
                  aria-label={`Anteil Teil ${i + 1} in Prozent`}
                />
                <span className="text-[14px]">%</span>
                <span className="w-24 text-right text-[13px] tabular-nums text-ios-muted">
                  {vorschau ? formatCurrency(vorschau[i]!, currency) : "—"}
                </span>
                <button type="button" className="text-ios-red px-1 disabled:opacity-30" disabled={zeilen.length <= 2}
                  onClick={() => setZeilen((alt) => alt.filter((_, j) => j !== i))} aria-label={`Teil ${i + 1} entfernen`}>×</button>
              </div>
            ))}
          </div>
          <button type="button" className="mt-2 text-[14px] text-ios-blue"
            onClick={() => setZeilen((alt) => [...alt, { steuerArtId: null, prozent: "0" }])}>+ Teil hinzufügen</button>
          <div className={`mt-3 text-[14px] ${summeBp === VOLL_BP ? "text-ios-green" : "text-ios-blue"}`}>
            Summe: {formatProzent(summeBp)} {summeBp === VOLL_BP ? "✓" : "— muss 100 % ergeben"}
          </div>
          {pruefung && summeBp === VOLL_BP && <div className="mt-1 text-[13px] text-ios-blue">{pruefung}</div>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="px-3 py-1.5 rounded-ios text-[15px]" onClick={onClose}>Abbrechen</button>
            <button type="button" className="px-3 py-1.5 rounded-ios bg-ios-blue text-white text-[15px] disabled:opacity-40"
              disabled={pruefung !== null || setAnteile.isPending} onClick={speichern}>Speichern</button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
